import _ from 'lodash';
import React, { useState, useRef, useEffect, useContext } from 'react';
import TextFieldSection from './TextFieldSection';
import TranscriptionTextFieldSection from './TranscriptionTextFieldSection';
import NumberFieldSection from './NumberFieldSection';
import FieldSetSection from './FieldSetSection';
import InlineFieldSetSection from './InlineFieldSetSection';
import {
  TextCollectionFieldType,
  TextCollectionField,
  TextCollectionAttachment,
  TextCollectionRankingOrderOption,
  UnitField,
  FieldSet,
  isFieldSet,
  isUnitField,
  LogBehaviourActions,
} from '@scale/shared/types/params/textcollection';
import {
  TextCollectionAnnotation,
  TextFieldAnnotation,
  UnitFieldAnnotation,
} from '@scale/shared/types/response/textcollection';
import { ChatTaskBehaviorLogMetadata } from '@scale/shared/types/chat';
import { Breadcrumb } from './breadcrumbs';
import { AutoCompleteQuery } from './autoComplete';
import { makeStyles } from 'design-system/styles';
import AutocompleteFieldSection from './AutocompleteFieldSection';
import QueryableTextAndResultsFieldSection from './QueryableTextAndResultsFieldSection';
import JSONFieldSection from './JSONFieldSection';
import CategoryFieldSection from './CategoryFieldSection';
import SliderFieldSection from './SliderFieldSection';
import DatetimeFieldSection from './DatetimeFieldSection';
import TimeRangeFieldSection from './TimeRangeFieldSection';
import SelectFieldSection from './SelectFieldSection';
import BooleanFieldSection from './BooleanFieldSection';
import BoundingBoxFieldSection from './BoundingBoxFieldSection';
import RankingOrderFieldSection from './RankingOrderFieldSection';
import CodeFieldSection from './CodeFieldSection';
import FileUploadFieldSection from './FileUploadFieldSection';
import { Checkbox, Tooltip } from 'design-system';
import { isBlankField } from '@scale/shared/textcollection/helpers';
import { analytics } from '@scale/frontend-lib/analytics/segment-source-scalers';
import { getDefaultAnnotation } from '@scale/shared/textcollection/defaults';
import HintTooltipIcon from './HintTooltipIcon';
import {
  TextCollectionSuggestionValue,
  TextCollectionAnnotationsOrCallback,
} from '@scale/shared/types/textCollection';
import { isInlineField, isInlineFieldSet } from '@scale/shared/textcollection/field';
import { getExtraContent } from './helperText';
// TODO: Import @material-ui from design-system folder
/* eslint-disable restrict-imports/restrict-imports */
import { FormHelperText } from '@material-ui/core';
import { LintIssue, LintSeverity } from '@scale/shared/task_lint/task_linter';
import { TextCollectionConfig } from '@scale/shared/defaults';
import clsx from 'clsx';
import Kbd from 'react-scale/components/design-system/Kbd';
import { FieldValidation } from '@scale/shared/types/rlhf';
import { WorkspaceSection } from 'react-scale/components/scalers/tasks/textcollection/WorkspaceSection';
import WfeButtonFieldSection from 'react-scale/components/scalers/tasks/textcollection/WfeButtonFieldSection';
import ResponseTemplatingPopper from 'react-scale/components/scalers/tasks/ThreadedInteraction/ResponseTemplatingPopper';
import { useChatStepChildIdContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatStepChildIdContext';
import { ChatTaskContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatTaskContext';
import TutorialFieldContainer from 'react-scale/components/scalers/tasks/tutorial/TutorialFieldContainer';
import { MarkdownFieldSection } from 'react-scale/components/scalers/tasks/textcollection/MarkdownFieldSection';
import { AudioRecordingFieldSection } from 'react-scale/components/scalers/tasks/textcollection/AudioRecording/AudioRecordingFieldSection';
import MultiChannelAudioUploadFieldSection from 'react-scale/components/scalers/tasks/textcollection/MultiChannelAudioUpload/MultiChannelAudioUploadFieldSection';

interface FieldSectionProps {
  field: TextCollectionField;
  annotation: TextCollectionAnnotation;
  currentAnnotations: Record<string, TextCollectionAnnotation>;
  isCustomer?: boolean;
  setAnnotation: (annotation: TextCollectionAnnotation) => void;
  setAnnotationIndex: (
    field: TextCollectionField,
    response: TextCollectionAnnotationsOrCallback,
    index: number,
  ) => void;
  deleteAnnotationIndex: (field: TextCollectionField, index: number) => void;
  pushAnnotations: (
    field: TextCollectionField,
    response: Record<string, TextCollectionAnnotation>,
  ) => void;
  addBreadcrumb: (breadcrumb: Breadcrumb) => void;
  autoComplete: (query: AutoCompleteQuery) => Record<string, TextCollectionAnnotation>[];
  disabled?: boolean;
  blocked?: boolean;
  quickAddResponses: Record<string, TextCollectionAnnotation>[];
  unitResponsesByField: Record<string, UnitFieldAnnotation>;
  previouslyVisitedChild?: Breadcrumb;
  suggestions?: TextCollectionSuggestionValue[];
  readOnlyFieldIds?: string[];
  fieldDisabledForPrompt?: boolean;
  bboxFieldToEdit: string | null;
  setBboxFieldToEdit: (fieldId: string | null) => void;
  attachment: TextCollectionAttachment;
  errors?: LintIssue[];
  warnings?: LintIssue[];
  assignmentId?: string;
  loggedUserId?: string;
  textCollectionConfig?: TextCollectionConfig;
  isAudit?: boolean;
  prefillKey?: string;
  setSingleAnnotation: (annotation: TextCollectionAnnotation) => void;
  rankingOrderOptions?: TextCollectionRankingOrderOption[];
  projectId?: string;
  index?: number;
  previousFieldAnnotations?: TextCollectionAnnotation[];
  dolphinSetEnhancedResponse?: {
    setEnhancedResponseFn: (annotation: TextCollectionAnnotation) => void;
    generatedResponseA: TextCollectionAnnotation;
    generatedResponseB: TextCollectionAnnotation;
    hasEnhancedResponseField: boolean;
  };
  handleValidateField?: (annotation: TextCollectionAnnotation) => void;
  setFieldIdPendingValidation?: (fieldId: string | undefined) => void;
  setSubmitCodeError?: (field_id: string, error: string) => void;
  fieldIdPendingValidation?: string;
  fieldValidationResult?: FieldValidation[];
  fieldValidationEnabled?: boolean;
  codingFields?: TextCollectionAnnotation[];
  logBehaviourActions?: LogBehaviourActions;
  isTestQuestion?: boolean;
  chatTaskBehaviorLogMetadata?: ChatTaskBehaviorLogMetadata;
  readOnlyMessage?: string;
  useSimpleCodeField?: boolean;
}

const shouldBeVisible = (
  field: TextCollectionField,
  annotation: TextCollectionAnnotation,
  suggestions?: TextCollectionSuggestionValue[],
): boolean =>
  !isInlineField(field) ||
  !field.toggle_message ||
  !isBlankField(annotation) ||
  !!suggestions?.length;

const useActiveElement = () => {
  const [active, setActive] = useState(document.activeElement);

  const handleFocusIn = () => {
    setActive(document.activeElement);
  };

  useEffect(() => {
    document.addEventListener('focusin', handleFocusIn);
    return () => {
      document.removeEventListener('focusin', handleFocusIn);
    };
  }, []);

  return active;
};

// eslint-disable-next-line complexity
export default function FieldSection(props: FieldSectionProps) {
  const styles = useFormSectionStyles();

  const active = useActiveElement();
  const ref = useRef<HTMLDivElement>(null);

  const [isFocused, setIsFocused] = useState(false);
  const [tooltipVisible, setTooltipVisible] = useState(false);

  const { childId, metadata } = useChatStepChildIdContext();
  const isChatPreviewMode = useContext(ChatTaskContext)?.previewMode;

  useEffect(() => {
    if (!props.textCollectionConfig?.useTextCollectionShortcuts) {
      return;
    }

    if (
      (ref.current === active?.childNodes[0] ||
        ref.current === active ||
        ref.current?.contains(active)) &&
      !isFocused
    ) {
      setIsFocused(true);
    } else if (
      ref.current !== active?.childNodes[0] &&
      ref.current !== active &&
      !ref.current?.contains(active) &&
      isFocused
    ) {
      setIsFocused(false);
    }
  }, [active, isFocused]);

  const [visible, setVisible] = React.useState<boolean>(
    shouldBeVisible(props.field, props.annotation, props.suggestions),
  );
  const [hasShownFromSuggestions, setHasShownFromSuggestions] = React.useState<boolean>(
    !!props.suggestions?.length,
  );
  const toggleVisible = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.checked;
    analytics.track('TextCollection Field Toggled', {
      field_id: props.field.field_id,
      value,
    });
    if (!value) {
      props.setAnnotation(getDefaultAnnotation(props.field));
    }
    setVisible(value);
  };
  React.useEffect(() => {
    if (!visible && props.suggestions?.length && !hasShownFromSuggestions) {
      setHasShownFromSuggestions(true);
      setVisible(true);
    }
  }, [props.suggestions, visible, setVisible, hasShownFromSuggestions]);

  React.useEffect(() => {
    setVisible(shouldBeVisible(props.field, props.annotation, props.suggestions));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.field, props.annotation]);

  const disableField =
    props.disabled ||
    props.blocked ||
    props.readOnlyFieldIds?.includes(props.field.field_id) ||
    props.fieldDisabledForPrompt;
  const extraContent = (props.field as UnitField | FieldSet).extra_content;

  const sectionSeparator = (isUnitField(props.field) || isFieldSet(props.field)) &&
    props.field.show_separator && <hr className={styles.sectionSeparator} />;

  const [fieldValidationErrors, fieldValidationWarnings] = _.partition(
    props.fieldValidationResult ?? [],
    { severity: LintSeverity.ERROR },
  );

  const disableTooltipOverride = !tooltipVisible || !props.fieldValidationEnabled;
  const tooltipIsDisabled = (!disableField && disableTooltipOverride) || isChatPreviewMode;
  const FieldSectionComponent = (
    <>
      {visible && (
        <>
          {props.index !== undefined && (
            <Kbd style={{ marginRight: 8, marginBottom: 8 }}>{props.index}</Kbd>
          )}
          {sectionSeparator}
          {props.annotation.isFixed && <div style={{ color: '#01579B' }}>(CUSTOMER FIXED)</div>}

          {props.field.type === TextCollectionFieldType.FieldSet &&
            props.annotation.type === TextCollectionFieldType.FieldSet &&
            !props.field.inline && (
              <FieldSetSection
                field={props.field}
                annotation={props.annotation}
                setAnnotation={props.setAnnotation}
                addBreadcrumb={props.addBreadcrumb}
                disabled={disableField}
                quickAddResponses={props.quickAddResponses}
                autoComplete={props.autoComplete}
                previouslyVisitedChild={props.previouslyVisitedChild}
                attachment={props.attachment}
              />
            )}
          {isInlineFieldSet(props.field) &&
            props.annotation.type === TextCollectionFieldType.FieldSet && (
              <InlineFieldSetSection
                field={props.field}
                annotation={props.annotation}
                setAnnotationIndex={props.setAnnotationIndex}
                setAnnotation={props.setAnnotation}
                deleteAnnotationIndex={props.deleteAnnotationIndex}
                pushAnnotations={props.pushAnnotations}
                disabled={disableField}
                quickAddResponses={props.quickAddResponses}
              />
            )}
          {props.field.type === TextCollectionFieldType.Text &&
            props.annotation.type === TextCollectionFieldType.Text && (
              <TextFieldSection
                isTestQuestion={props.isTestQuestion}
                field={props.field}
                projectId={props.projectId}
                assignmentId={props.assignmentId}
                userId={props.loggedUserId}
                annotation={props.annotation}
                setAnnotation={props.setAnnotation}
                disabled={disableField}
                prefillKey={props.prefillKey}
                setSingleAnnotation={props.setSingleAnnotation}
                previousTextFieldAnnotations={
                  props.previousFieldAnnotations as TextFieldAnnotation[]
                }
                handleValidateField={
                  props.handleValidateField &&
                  (() => {
                    props.handleValidateField?.(props.annotation);
                  })
                }
                setFieldIdPendingValidation={props.setFieldIdPendingValidation}
                fieldIdPendingValidation={props.fieldIdPendingValidation}
                codingFields={props.codingFields}
                logBehaviourActions={props.logBehaviourActions}
                chatTaskBehaviorLogMetadata={props.chatTaskBehaviorLogMetadata}
              />
            )}
          {props.field.type === TextCollectionFieldType.Autocomplete &&
            props.annotation.type === TextCollectionFieldType.Autocomplete && (
              <AutocompleteFieldSection
                field={props.field}
                annotation={props.annotation}
                setAnnotation={props.setAnnotation}
                disabled={disableField}
                textCollectionConfig={props.textCollectionConfig}
                projectId={props.projectId}
              />
            )}
          {props.field.type === TextCollectionFieldType.QueryableTextAndResults &&
            props.annotation.type === TextCollectionFieldType.QueryableTextAndResults && (
              <QueryableTextAndResultsFieldSection
                field={props.field}
                annotation={props.annotation}
                setAnnotation={props.setAnnotation}
              />
            )}
          {props.field.type === TextCollectionFieldType.JSON &&
            props.annotation.type === TextCollectionFieldType.JSON && (
              <JSONFieldSection
                field={props.field}
                annotation={props.annotation}
                setAnnotation={props.setAnnotation}
              />
            )}
          {props.field.type === TextCollectionFieldType.RankingOrder &&
            props.annotation.type === TextCollectionFieldType.RankingOrder && (
              <RankingOrderFieldSection
                rankingOrderOptions={props.rankingOrderOptions}
                field={props.field}
                annotation={props.annotation}
                setAnnotation={props.setAnnotation}
                disabled={disableField}
              />
            )}
          {props.field.type === TextCollectionFieldType.Code &&
            props.annotation.type === TextCollectionFieldType.Code && (
              <CodeFieldSection
                useSimpleCodeField={props.useSimpleCodeField}
                field={props.field}
                projectId={props.projectId}
                assignmentId={props.assignmentId}
                userId={props.loggedUserId}
                annotation={props.annotation}
                isCustomer={props.isCustomer}
                setAnnotation={props.setAnnotation}
                setSubmitError={props.setSubmitCodeError}
                disabled={disableField}
              />
            )}
          {props.field.type === TextCollectionFieldType.WorkspaceContainer &&
            props.annotation.type === TextCollectionFieldType.WorkspaceContainer && (
              <WorkspaceSection
                field={props.field}
                projectId={props.projectId}
                assignmentId={props.assignmentId}
                userId={props.loggedUserId}
                annotation={props.annotation}
                isCustomer={props.isCustomer}
                setAnnotation={props.setAnnotation}
                setSubmitError={props.setSubmitCodeError}
                disabled={disableField}
              />
            )}
          {props.field.type === TextCollectionFieldType.WfeButton &&
            props.annotation.type === TextCollectionFieldType.WfeButton && (
              <WfeButtonFieldSection
                field={props.field}
                annotation={props.annotation}
                setAnnotation={props.setAnnotation}
                disabled={disableField}
              />
            )}
          {props.field.type === TextCollectionFieldType.TranscriptionText &&
            props.annotation.type === TextCollectionFieldType.TranscriptionText && (
              <TranscriptionTextFieldSection
                field={props.field}
                annotation={props.annotation}
                setAnnotation={props.setAnnotation}
                disabled={disableField}
              />
            )}
          {props.field.type === TextCollectionFieldType.Number &&
            props.annotation.type === TextCollectionFieldType.Number &&
            !props.field.use_slider && (
              <NumberFieldSection
                field={props.field}
                annotation={props.annotation}
                setAnnotation={props.setAnnotation}
                disabled={disableField}
              />
            )}
          {props.field.type === TextCollectionFieldType.Number &&
            props.annotation.type === TextCollectionFieldType.Number &&
            props.field.use_slider && (
              <SliderFieldSection
                field={props.field}
                annotation={props.annotation}
                setAnnotation={props.setAnnotation}
                currentAnnotations={props.currentAnnotations}
                disabled={disableField}
              />
            )}
          {props.field.type === TextCollectionFieldType.Category &&
            props.annotation.type === TextCollectionFieldType.Category && (
              <CategoryFieldSection
                field={props.field}
                annotation={props.annotation}
                currentAnnotations={props.currentAnnotations}
                setAnnotation={props.setAnnotation}
                disabled={disableField}
                suggestions={props.suggestions}
                assignmentId={props.assignmentId}
                loggedUserId={props.loggedUserId}
                textCollectionConfig={props.textCollectionConfig}
                isAudit={props.isAudit}
                isFocused={isFocused}
                dolphinSetEnhancedResponse={props.dolphinSetEnhancedResponse}
                projectId={props.projectId}
              />
            )}
          {props.field.type === TextCollectionFieldType.Datetime &&
            props.annotation.type === TextCollectionFieldType.Datetime && (
              <DatetimeFieldSection
                field={props.field}
                annotation={props.annotation}
                setAnnotation={props.setAnnotation}
                disabled={disableField}
              />
            )}
          {props.field.type === TextCollectionFieldType.TimeRange &&
            props.annotation.type === TextCollectionFieldType.TimeRange && (
              <TimeRangeFieldSection
                field={props.field}
                annotation={props.annotation}
                setAnnotation={props.setAnnotation}
                disabled={disableField}
                unitResponsesByField={props.unitResponsesByField}
              />
            )}
          {props.field.type === TextCollectionFieldType.Select &&
            props.annotation.type === TextCollectionFieldType.Select && (
              <SelectFieldSection
                field={props.field}
                annotation={props.annotation}
                setAnnotation={props.setAnnotation}
                disabled={disableField}
              />
            )}
          {props.field.type === TextCollectionFieldType.Boolean &&
            props.annotation.type === TextCollectionFieldType.Boolean && (
              <BooleanFieldSection
                field={props.field}
                annotation={props.annotation}
                setAnnotation={props.setAnnotation}
                disabled={disableField}
              />
            )}
          {props.field.type === TextCollectionFieldType.BoundingBox &&
            props.annotation.type === TextCollectionFieldType.BoundingBox && (
              <BoundingBoxFieldSection
                field={props.field}
                annotation={props.annotation}
                bboxFieldToEdit={props.bboxFieldToEdit}
                setBboxFieldToEdit={props.setBboxFieldToEdit}
                disabled={disableField}
              />
            )}
          {props.field.type === TextCollectionFieldType.FileUpload &&
            props.annotation.type === TextCollectionFieldType.FileUpload && (
              <FileUploadFieldSection
                field={props.field}
                annotation={props.annotation}
                setAnnotation={props.setAnnotation}
                disabled={disableField}
                assignmentId={props.assignmentId}
              />
            )}
          {props.chatTaskBehaviorLogMetadata &&
            !_.isNil(props.chatTaskBehaviorLogMetadata.responseIndex) && (
              <TutorialFieldContainer
                fieldId={props.field.field_id}
                stepIndex={props.chatTaskBehaviorLogMetadata.responseIndex}
                response={
                  props.annotation.response.length === 0 ? '' : props.annotation.response[0]
                }
                candidateId={props.chatTaskBehaviorLogMetadata.candidateId}
              />
            )}
          {props.field.type === TextCollectionFieldType.Markdown &&
            props.annotation.type === TextCollectionFieldType.Markdown && (
              <MarkdownFieldSection
                field={props.field}
                annotation={props.annotation}
                setAnnotation={props.setAnnotation}
                disabled={disableField}
                assignmentId={props.assignmentId}
              />
            )}
          {props.field.type === TextCollectionFieldType.AudioRecording &&
            props.annotation.type === TextCollectionFieldType.AudioRecording && (
              <AudioRecordingFieldSection
                annotation={props.annotation}
                disabled={disableField}
                field={props.field}
                setAnnotation={props.setAnnotation}
              />
            )}
          {props.field.type === TextCollectionFieldType.MultiChannelAudioUpload &&
            props.annotation.type === TextCollectionFieldType.MultiChannelAudioUpload && (
              <MultiChannelAudioUploadFieldSection
                field={props.field}
                annotation={props.annotation}
                setAnnotation={props.setAnnotation}
                disabled={disableField}
                assignmentId={props.assignmentId}
              />
            )}
        </>
      )}
    </>
  );

  return (
    <div className={clsx(styles.fieldItem, { focused: !!isFocused })} ref={ref}>
      {(props.errors || props.warnings || !!props.fieldValidationResult?.length) && (
        <div className={styles.lintIssuesContainer}>
          {(props.errors || !!fieldValidationErrors.length) &&
            _.concat(props.errors ?? [], fieldValidationErrors).map((issue, i) => (
              <div key={`${issue.id}-${i}`}>
                ❌<span>&nbsp;&nbsp;</span>
                {issue.display}
              </div>
            ))}
          {(props.warnings || !!fieldValidationWarnings.length) &&
            _.concat(props.warnings ?? [], fieldValidationWarnings).map(issue => (
              <div key={issue.id}>
                ⚠️<span>&nbsp;&nbsp;</span>
                {issue.display}
              </div>
            ))}
        </div>
      )}
      {isInlineField(props.field) && props.field.toggle_message && (
        <div className={styles.toggleCheckboxContainer}>
          <Checkbox
            label={props.field.toggle_message}
            checked={visible}
            onChange={toggleVisible}
            disabled={disableField}
          />
          {props.field.toggle_hint && <HintTooltipIcon hint={props.field.toggle_hint} />}
        </div>
      )}
      {extraContent && (
        <FormHelperText classes={{ root: styles.extraContent }}>
          {getExtraContent(extraContent)}
        </FormHelperText>
      )}
      <Tooltip
        disableHoverListener={tooltipIsDisabled}
        disableFocusListener={tooltipIsDisabled}
        disableTouchListener={tooltipIsDisabled}
        placement="top"
        title={
          <div className={styles.tooltip}>
            {props.blocked
              ? 'Please complete all attachments before continuing'
              : props.fieldDisabledForPrompt
              ? `Validate ${props.fieldIdPendingValidation ?? 'current field'} before continuing`
              : props.readOnlyMessage
              ? props.readOnlyMessage
              : 'The answer for this field is already provided.'}
          </div>
        }
        onClose={() => setTooltipVisible(false)}
        onOpen={() => setTooltipVisible(true)}
      >
        {childId ? (
          <div className={styles.row}>
            <div className={styles.grow}>{FieldSectionComponent}</div>
            {childId && (
              <ResponseTemplatingPopper
                childId={childId}
                metadata={{
                  ...(metadata ?? {}),
                  fieldId: props.field.field_id,
                  numberFieldScale:
                    props.field.type === TextCollectionFieldType.Number &&
                    props.field.min !== undefined &&
                    props.field.max !== undefined
                      ? {
                          min: props.field.min,
                          max: props.field.max,
                        }
                      : undefined,
                }}
                options={{ disablePortal: false }}
              />
            )}
          </div>
        ) : (
          <div>{FieldSectionComponent}</div>
        )}
      </Tooltip>
    </div>
  );
}

const useFormSectionStyles = makeStyles(() => {
  return {
    fieldItem: {
      padding: 8,
      display: 'flex',
      flexDirection: 'column',
      '&.focused': {
        borderLeft: '4px solid blue',
      },
      whiteSpace: 'pre-line',
    },
    toggleCheckbox: {
      paddingLeft: 0,
    },
    toggleCheckboxContainer: {
      marginBottom: 8,
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
    },
    tooltip: {
      maxWidth: 496,
    },
    extraContent: {
      marginBottom: 16,
      fontSize: '14px',
      lineHeight: '20px',
      color: '#000000',
    },
    sectionSeparator: {
      border: '2px solid lightgray',
      margin: '1em auto',
    },
    lintIssuesContainer: {
      marginBottom: 8,
    },
    row: {
      display: 'flex',
      flexDirection: 'row',
    },
    grow: {
      flexGrow: 1,
    },
  };
});
